/**
 * Números de dano sobre a cabeça. Paleta e pulo vêm de /combat/dano.json;
 * se o arquivo falhar, o fallback abaixo é a cópia literal dele.
 */

export type TipoDano = 'normal' | 'critico' | 'veneno' | 'magia' | 'cura';
export type KindGolpe = 'melee' | 'magic' | 'poison' | 'cura';

type ParCor = { fill: string; stroke: string };

export type CfgDano = {
  fonte: string;
  fonteCritico: string;
  borda: number;
  subidaPx: number;
  duracaoMs: number;
  fadeMs: number;
  nascimentoY: number;
  desvioX: number;
  teto: number;
  cores: Record<TipoDano, ParCor>;
  sofridoFill: string;
  sofridoBordas: Record<TipoDano, string>;
};

const FALLBACK: CfgDano = {
  fonte: '700 16px Tahoma, Segoe UI, sans-serif',
  fonteCritico: '700 20px Tahoma, Segoe UI, sans-serif',
  borda: 3,
  subidaPx: 28,
  duracaoMs: 800,
  fadeMs: 220,
  nascimentoY: 18,
  desvioX: 12,
  teto: 24,
  cores: {
    normal:  { fill: '#ffffff', stroke: '#000000' },
    critico: { fill: '#ffe066', stroke: '#000000' },
    veneno:  { fill: '#c06bff', stroke: '#000000' },
    magia:   { fill: '#5b9fff', stroke: '#000000' },
    cura:    { fill: '#4ecf6a', stroke: '#000000' }
  },
  sofridoFill: '#e04a3f',
  sofridoBordas: {
    normal: '#ffffff',
    critico: '#ffe066',
    veneno: '#c06bff',
    magia: '#5b9fff',
    cura: '#4ecf6a'
  }
};

const HEX = /^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/;
const TIPOS: readonly TipoDano[] = ['normal', 'critico', 'veneno', 'magia', 'cura'];

type Floater = {
  idAlvo: number;
  n: number;
  tipo: TipoDano;
  sofrido: boolean;
  nasceu: number;
  desvio: number;
  texto?: string;
};

const pool: Floater[] = [];
const cabecas = new Map<number, { x: number; y: number }>();
let cfg: CfgDano = FALLBACK;
let carregando: Promise<void> | null = null;

function numPos(v: unknown, fb: number): number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fb;
}

function hexOk(v: unknown, fb: string): string {
  return typeof v === 'string' && HEX.test(v) ? v : fb;
}

function corOk(v: unknown, fb: ParCor): ParCor {
  if (!v || typeof v !== 'object') return fb;
  const o = v as { fill?: unknown; stroke?: unknown };
  return { fill: hexOk(o.fill, fb.fill), stroke: hexOk(o.stroke, fb.stroke) };
}

function lerSofrido(raw: unknown): Pick<CfgDano, 'sofridoFill' | 'sofridoBordas'> {
  if (!raw || typeof raw !== 'object') {
    return { sofridoFill: FALLBACK.sofridoFill, sofridoBordas: { ...FALLBACK.sofridoBordas } };
  }
  const o = raw as Record<string, unknown>;
  const bordasRaw = (o.bordas && typeof o.bordas === 'object') ? o.bordas as Record<string, unknown> : {};
  const sofridoBordas = { ...FALLBACK.sofridoBordas };
  for (const t of TIPOS) sofridoBordas[t] = hexOk(bordasRaw[t], FALLBACK.sofridoBordas[t]);
  return { sofridoFill: hexOk(o.fill, FALLBACK.sofridoFill), sofridoBordas };
}

function lerCfg(raw: unknown): CfgDano {
  if (!raw || typeof raw !== 'object') return FALLBACK;
  const o = raw as Record<string, unknown>;
  const coresRaw = (o.cores && typeof o.cores === 'object') ? o.cores as Record<string, unknown> : {};
  const cores = { ...FALLBACK.cores };
  for (const t of TIPOS) cores[t] = corOk(coresRaw[t], FALLBACK.cores[t]);
  const teto = Math.min(64, Math.round(numPos(o.teto, FALLBACK.teto)));
  const sofrido = lerSofrido(o.sofrido);
  return {
    fonte: typeof o.fonte === 'string' && o.fonte.trim() ? o.fonte : FALLBACK.fonte,
    fonteCritico: typeof o.fonteCritico === 'string' && o.fonteCritico.trim() ? o.fonteCritico : FALLBACK.fonteCritico,
    borda: numPos(o.borda, FALLBACK.borda),
    subidaPx: numPos(o.subidaPx, FALLBACK.subidaPx),
    duracaoMs: numPos(o.duracaoMs, FALLBACK.duracaoMs),
    fadeMs: numPos(o.fadeMs, FALLBACK.fadeMs),
    nascimentoY: numPos(o.nascimentoY, FALLBACK.nascimentoY),
    desvioX: numPos(o.desvioX, FALLBACK.desvioX),
    teto: Math.max(1, teto),
    cores,
    sofridoFill: sofrido.sofridoFill,
    sofridoBordas: sofrido.sofridoBordas
  };
}

export function carregarDano(): Promise<void> {
  if (carregando) return carregando;
  carregando = fetch('/combat/dano.json')
    .then(res => (res.ok ? res.json() : Promise.reject()))
    .then(data => { cfg = lerCfg(data); })
    .catch(() => { cfg = FALLBACK; });
  return carregando;
}

export function tipoDoStrike(kind: KindGolpe, crit: boolean): TipoDano {
  if (kind === 'cura') return 'cura';
  if (crit) return 'critico';
  if (kind === 'poison') return 'veneno';
  if (kind === 'magic') return 'magia';
  return 'normal';
}

export function spawnDano(
  idAlvo: number, n: number, tipo: TipoDano, sofrido = false, agora = performance.now()
): void {
  if (n <= 0) return;
  const desvio = (Math.random() * 2 - 1) * cfg.desvioX;
  pool.push({ idAlvo, n, tipo, sofrido, nasceu: agora, desvio });
  while (pool.length > cfg.teto) pool.shift();
}

export function spawnTexto(idAlvo: number, texto: string, agora = performance.now()): void {
  const desvio = (Math.random() * 2 - 1) * cfg.desvioX;
  pool.push({ idAlvo, n: 0, tipo: 'cura', sofrido: false, nasceu: agora, desvio, texto });
  while (pool.length > cfg.teto) pool.shift();
}

/**
 * Aviso parado sobre a cabeça, como a mensagem local do UO (LocalOverheadMessage): "Você está atacando X!" em
 * vermelho, hue 0x22. Fica alguns segundos e some; um por pessoa (o novo troca o antigo).
 */
type AvisoCabeca = { idAlvo: number; texto: string; cor: string; nasceu: number; ate: number };
const avisos: AvisoCabeca[] = [];
const AVISO_MS = 3500;
const AVISO_FADE_MS = 400;

export function avisarSobre(idAlvo: number, texto: string, cor = '#ff3b2f', agora = performance.now()): void {
  const limpo = texto.trim();
  if (!limpo) return;
  for (let i = avisos.length - 1; i >= 0; i--) if (avisos[i].idAlvo === idAlvo) avisos.splice(i, 1);
  avisos.push({ idAlvo, texto: limpo, cor, nasceu: agora, ate: agora + AVISO_MS });
}

function desenharAvisos(ctx: CanvasRenderingContext2D, zoom: number, agora: number): void {
  let i = 0;
  while (i < avisos.length) {
    const a = avisos[i];
    if (agora >= a.ate) {
      avisos.splice(i, 1);
      continue;
    }
    const cab = cabecas.get(a.idAlvo);
    if (!cab) { i++; continue; }
    const falta = a.ate - agora;
    ctx.globalAlpha = falta < AVISO_FADE_MS ? falta / AVISO_FADE_MS : 1;
    ctx.font = '700 13px Tahoma, Segoe UI, sans-serif';
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#000000';
    ctx.fillStyle = a.cor;
    const y = cab.y - (cfg.nascimentoY + 22) * zoom;
    ctx.strokeText(a.texto, cab.x, y);
    ctx.fillText(a.texto, cab.x, y);
    i++;
  }
}

export function marcarCabeca(id: number, x: number, y: number): void {
  cabecas.set(id, { x, y });
}

export function desenharDano(
  ctx: CanvasRenderingContext2D,
  zoom: number,
  agora: number
): void {
  if (pool.length === 0 && avisos.length === 0) return;
  const dur = cfg.duracaoMs;
  const fade = Math.min(cfg.fadeMs, dur);
  const fadeAt = dur - fade;
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.lineJoin = 'round';
  ctx.lineWidth = cfg.borda;
  let i = 0;
  while (i < pool.length) {
    const f = pool[i];
    const age = agora - f.nasceu;
    if (age >= dur) {
      pool.splice(i, 1);
      continue;
    }
    const cab = cabecas.get(f.idAlvo);
    if (!cab) {
      i++;
      continue;
    }
    const t = age / dur;
    const x = cab.x + f.desvio;
    const y = cab.y - cfg.nascimentoY * zoom - cfg.subidaPx * zoom * t;
    const alfa = age < fadeAt ? 1 : 1 - (age - fadeAt) / fade;
    const sofrido = f.sofrido && f.tipo !== 'cura';
    const fill = sofrido ? cfg.sofridoFill : cfg.cores[f.tipo].fill;
    const stroke = sofrido ? cfg.sofridoBordas[f.tipo] : cfg.cores[f.tipo].stroke;
    ctx.globalAlpha = alfa;
    ctx.font = f.tipo === 'critico' ? cfg.fonteCritico : cfg.fonte;
    ctx.strokeStyle = stroke;
    ctx.fillStyle = fill;
    const txt = f.texto ?? String(f.n);
    ctx.strokeText(txt, x, y);
    ctx.fillText(txt, x, y);
    i++;
  }
  desenharAvisos(ctx, zoom, agora);
  ctx.restore();
}
